import React, { useEffect } from 'react';
import { CatalogDuration, CatalogDurationUnit, CourseProps, LearningActivityStatus, RegistrationStatus, Schedule } from './types';
import { useSearchParams } from 'react-router-dom';
import { useSaveIltEvaluationResponse } from '../hooks/evaluation/useSaveIltEvaluationResponse';
import { ApplicationUser } from '../hooks/auth/useAuth';

export enum B2IOrganizationType {
    CUSTOMERS_B2I = 'CUSTOMERS_B2I',
    PARTNERS_B2I = 'PARTNERS_B2I',
}

/** only CUSTOMERS_B2I since we need to fetch registration counter for PARTNERS_B2I */
export enum B2IOrganizationTypeForRegistrationCounter {
    CUSTOMERS_B2I = 'CUSTOMERS_B2I',
}

export enum PartnerOrganizationType {
    PARTNERS_B2I = 'PARTNERS_B2I',
    PARTNERS_B2B = 'PARTNERS_B2B',
}

export const getBreadcrumbItemsFromLocation = (location: string) => {
    return location.split('/').map((item) => {
        return { text: item.charAt(0).toUpperCase() + item.slice(1), href: location.split(item)[0] + item };
    });
};

export const getBreadcrumbItemsFromCourse = (title: string) => {
    return [
        { text: 'Home', href: '/' },
        { text: title, href: '#' },
    ];
};

export const getWeekDay = (date: Date) => {
    const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return WEEKDAYS[date.getDay()];
};

export const daysUntilStart = (date: Date) => {
    const MILLISECONDS_PER_DAY = 86400000; // = 1000 * 60 * 60 * 24
    return Math.floor((date.getTime() - Date.now()) / MILLISECONDS_PER_DAY);
};

export const getRemainingSeats = (seats: number) => {
    return seats === 0 ? 'No available seats' : `${seats} ${seats === 1 ? 'seat' : 'seats'} left`;
};

export const createList = (items?: string[]) => {
    if (!items || !items.length) {
        return null;
    }
    return (
        <ul>
            {items.map((item) => (
                <li key={item}>{item}</li>
            ))}
        </ul>
    );
};

export const enum Tab {
    Available = 'Available',
    Enrolled = 'Enrolled',
    Waitlisted = 'Waitlisted',
    Past = 'Past',
    NotVisible = 'NotVisible',
}
const shouldShowPastClass = (props: CourseProps): boolean => {
    if (!props.registrationStatus) {
        return false;
    }

    return (
        (props.registrationStatus === RegistrationStatus.REGISTERED ||
            props.registrationStatus === RegistrationStatus.CANCELED ||
            props.registrationStatus === RegistrationStatus.WAITLISTED) === true
    );
};
export const courseClassifier = (props: CourseProps): Tab => {
    switch (props.courseStatus) {
        case LearningActivityStatus.ACTIVE:
            if (isBeforeClassEndTime(props)) {
                if (props.registrationStatus === RegistrationStatus.REGISTERED) {
                    return Tab.Enrolled;
                } else if (props.registrationStatus === RegistrationStatus.WAITLISTED) {
                    return Tab.Waitlisted;
                } else {
                    return Tab.Available;
                }
            } else {
                props.courseStatus = LearningActivityStatus.COMPLETED;
                return shouldShowPastClass(props) ? Tab.Past : Tab.NotVisible;
            }
        case LearningActivityStatus.CANCELED:
        case LearningActivityStatus.COMPLETED:
            return shouldShowPastClass(props) ? Tab.Past : Tab.NotVisible;
        default:
            return Tab.NotVisible;
    }
};

export const availableCourses = (courses: CourseProps[]): CourseProps[] => {
    return courses.filter((course) => courseClassifier(course) === Tab.Available);
};

export const waitlistedCourses = (courses: CourseProps[]): CourseProps[] => {
    return courses.filter((course) => courseClassifier(course) === Tab.Waitlisted);
};

export const enrolledCourses = (courses: CourseProps[]): CourseProps[] => {
    return courses.filter((course) => courseClassifier(course) === Tab.Enrolled);
};

export const pastCourses = (courses: CourseProps[]): CourseProps[] => {
    return courses.filter((course) => courseClassifier(course) === Tab.Past);
};

export const useSaveSurveyResponse = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const transcriptId = searchParams.get('transcriptid') ?? '';
    const responseId = searchParams.get('responseid') ?? '';
    const [saveResponse] = useSaveIltEvaluationResponse(transcriptId, responseId);
    useEffect(() => {
        if (transcriptId && responseId) {
            saveResponse().then(() => setSearchParams());
        }
    }, [responseId, transcriptId]);
};

/**
 * Checks if the current time is at least 15 minutes before the class start time
 * @param startDateTime The start date and time of the class
 * @returns true if current time is past 15 minutes before class start time (i.e., within 15 minutes of start or after start), false otherwise
 */
export const isPast15MinutesBeforeStart = (startDateTime: Date): boolean => {
    const currentTime = Date.now();
    const fifteenMinutesBeforeStart = new Date(startDateTime);
    fifteenMinutesBeforeStart.setMinutes(fifteenMinutesBeforeStart.getMinutes() - 15);

    // Return true if current time is within 15 minutes of start time or after start time
    return currentTime >= fifteenMinutesBeforeStart.getTime();
};

/**
 * Checks if the current time is before the class end time
 * @param date The end date and time of the class
 * @returns true if current time is before class end time, false otherwise
 */
export const isBefore = (date: Date): boolean => {
    const currentTime = Date.now();

    // Return true if current time is before end time
    return currentTime < date.getTime();
};

export const isAfter = (date: Date): boolean => {
    const currentTime = Date.now();

    // Return true if current time is after start time
    return currentTime >= date.getTime();
};

function is2HoursBeforeStartTime(date: Date): boolean {
    const currentTime = Date.now();
    const twoHoursBeforeStart = new Date(date);
    twoHoursBeforeStart.setMinutes(twoHoursBeforeStart.getMinutes() - 120);

    // Return true if current time is within 2 hours of start time or after start time
    return currentTime >= twoHoursBeforeStart.getTime();
}

export const isCourse2HoursBeforeStartTime: (course: Pick<CourseProps, 'schedule'>) => boolean = (course) => {
    return course.schedule.classDays.some((day) => is2HoursBeforeStartTime(day.startDateTime));
};

export const isWithin15MinutesOfStart: (course: Pick<CourseProps, 'schedule'>) => boolean = (course) => {
    return course.schedule.classDays.some((day) => isPast15MinutesBeforeStart(day.startDateTime));
};

export const isAfterClassStartTime: (course: Pick<CourseProps, 'schedule'>) => boolean = (course) => {
    return course.schedule.classDays.some((day) => isAfter(day.startDateTime));
};

export const isBeforeClassEndTime: (course: Pick<CourseProps, 'schedule'>) => boolean = (course) => {
    return course.schedule.classDays.some((day) => isBefore(day.endDateTime));
};

export const isAfterClassEndTime: (course: Pick<CourseProps, 'schedule'>) => boolean = (course) => {
    return course.schedule.classDays.some((day) => isAfter(day.endDateTime));
};

export const isWithinClassStartTimeAndEndTime: (course: Pick<CourseProps, 'schedule'>) => boolean = (course) => {
    return isAfterClassStartTime(course) && isBeforeClassEndTime(course);
};

/**
 * Calculate the actual duration of a course based on its schedule
 * @param schedule The course schedule containing class days
 * @returns A formatted string representing the actual duration (e.g., "1 day", "3 days")
 */
export const calculateActualDuration = (schedule: Schedule): string => {
    if (!schedule || !schedule.classDays || schedule.classDays.length === 0) {
        return 'N/A';
    }

    // Count the number of unique days in the schedule
    const uniqueDays = new Set<string>();

    schedule.classDays.forEach((day) => {
        // Format date as YYYY-MM-DD to count unique days
        const dateString = day.startDateTime.toISOString().split('T')[0];
        uniqueDays.add(dateString);
    });

    const dayCount = uniqueDays.size;
    return `${dayCount} ${dayCount === 1 ? 'day' : 'days'}`;
};

/**
 * Checks if a user belongs to a B2I organization type
 * @param user The ApplicationUser to check
 * @returns true if the user's organizationType is in the OrganizationType enum, false otherwise
 */
export const isUserB2I = (user: ApplicationUser): boolean => {
    if (!user || !user.organizationType) {
        return false;
    }
    return Object.values(B2IOrganizationTypeForRegistrationCounter).includes(
        user.organizationType as B2IOrganizationTypeForRegistrationCounter,
    );
};

/**
 * Check if we should query registration counter
 * @param user The ApplicationUser to check
 * @returns true if the user is not a B2I user, false otherwise
 */
export const shouldQueryRegistrationCounter = (user: ApplicationUser): boolean => {
    return !isUserB2I(user);
};

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Calendar days until access expires, or null when there is no expiry to show.
 *
 * Display only — counted in whole calendar days, ignoring the time of day at either end, so the
 * figure holds steady all day instead of shifting as the clock passes each 24 hour boundary. A
 * window closing late on Oct 1 therefore reads as 9 on Sep 22 rather than 10.
 *
 * Zero means access closes today and may still be usable; hasAccessExpired makes that call.
 */
export const getRemainingDays = (accessEndTimestamp?: number): number | null => {
    if (!accessEndTimestamp) return null;

    const endDay = new Date(accessEndTimestamp * 1000);
    endDay.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Rounded because a local day is 23 or 25 hours across a daylight saving change, which would
    // otherwise leave the division a fraction short or long of a whole day.
    const remainingDays = Math.round((endDay.getTime() - today.getTime()) / MILLISECONDS_PER_DAY);
    return remainingDays <= 0 ? 0 : remainingDays;
};

/**
 * Whether the access window has closed, to the second.
 *
 * Kept separate from the day-granular countdown above: on the closing day that reads zero while
 * access is still live, so gating on it would cut the last day short.
 */
export const hasAccessExpired = (accessEndTimestamp?: number): boolean => {
    if (!accessEndTimestamp) return false;
    return Math.floor(Date.now() / 1000) > accessEndTimestamp;
};

/** Short month and day from an epoch-seconds timestamp, e.g. "Feb 2". */
export const formatShortDate = (timestamp?: number): string => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Minutes to a readable duration. Zero means open-ended content rather than missing
 * data, so it renders as a dash instead of "0m".
 */
export const formatDuration = (durationMinutes: number): string => {
    if (!durationMinutes) return '—';
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (!hours) return `${minutes}m`;
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

/**
 * Month, day and year from an epoch-seconds timestamp, e.g. "Feb 2, 2026".
 *
 * Carries the year, unlike formatShortDate, for dates that have already passed and could otherwise
 * be read as this year's.
 */
export const formatFullDate = (timestamp?: number): string => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * A CatalogDuration as whole minutes, so formatDuration above can render it.
 *
 * Returns total minutes rather than hours and minutes so formatDuration keeps its
 * own presentation: whole hours stay "1h" rather than becoming "1h 0m", and zero stays a dash.
 *
 * Unit defaults to seconds when absent, matching how the catalog treats it.
 */
export const convertDurationToMinutes = (duration?: CatalogDuration): number => {
    if (!duration?.value) return 0;

    switch (duration.unit) {
        case CatalogDurationUnit.DAYS:
            return Math.floor(duration.value * 24 * 60);
        case CatalogDurationUnit.HOURS:
            return Math.floor(duration.value * 60);
        case CatalogDurationUnit.MINUTES:
            return Math.floor(duration.value);
        case CatalogDurationUnit.SECONDS:
        default:
            return Math.floor(duration.value / 60);
    }
};
